import React, { useState } from "react";
import {
  X,
  Shield,
  Search,
  Eye,
  AlertTriangle,
  CheckCircle,
  XCircle,
  User,
  CreditCard,
  Zap,
  Activity,
} from "lucide-react";

// Types
interface Alert {
  id: string;
  type: "fraud" | "kyc" | "transaction" | "system" | "compliance";
  severity: "low" | "medium" | "high" | "critical";
  status: "active" | "investigating" | "resolved" | "dismissed";
  title: string;
  description: string;
  relatedUser?: {
    id: string;
    name: string;
    email: string;
    country: string;
    countryFlag: string;
  };
  relatedTransaction?: {
    id: string;
    reference: string;
    amount: number;
    currency: string;
  };
  detectedAt: string;
  resolvedAt?: string;
  assignedTo?: string;
  riskScore: number;
  metadata: {
    ipAddress?: string;
    deviceInfo?: string;
    location?: string;
    reason: string;
    additionalInfo?: string;
  };
}

interface FilterState {
  search: string;
  type: string;
  severity: string;
  status: string;
  assignedTo: string;
}

interface AlertItemProps {
  alert: Alert;
  isSelected: boolean;
  onSelect: (alertId: string, checked: boolean) => void;
  onAction: (action: string, alert: Alert) => void;
}

interface AlertsListProps {
  alerts: Alert[];
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  selectedAlerts: string[];
  onSelectAlert: (alertId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onAlertAction: (action: string, alert: Alert) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  startIndex: number;
  endIndex: number;
  totalAlerts: number;
}

interface AlertDetailsProps {
  alert: Alert | null;
  onClose: () => void;
  onResolve: () => void;
  onDismiss: () => void;
  onInvestigate: () => void;
  onBlock: () => void;
}

// Utility Components
const TypeBadge: React.FC<{ type: Alert["type"] }> = ({ type }) => {
  const typeConfig = {
    fraud: { bg: "bg-red-100", text: "text-red-700", label: "Fraude", icon: Shield },
    kyc: { bg: "bg-blue-100", text: "text-blue-700", label: "KYC", icon: User },
    transaction: { bg: "bg-purple-100", text: "text-purple-700", label: "Transaction", icon: CreditCard },
    system: { bg: "bg-gray-100", text: "text-gray-700", label: "Système", icon: Activity },
    compliance: { bg: "bg-orange-100", text: "text-orange-700", label: "Compliance", icon: AlertTriangle },
  };

  const config = typeConfig[type];
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </span>
  );
};

const SeverityBadge: React.FC<{ severity: Alert["severity"] }> = ({ severity }) => {
  const severityConfig = {
    low: { bg: "bg-green-100", text: "text-green-700", label: "Faible" },
    medium: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Moyen" },
    high: { bg: "bg-orange-100", text: "text-orange-700", label: "Élevé" },
    critical: { bg: "bg-red-100", text: "text-red-700", label: "Critique" },
  };

  const config = severityConfig[severity];

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>{config.label}</span>
  );
};

const StatusBadge: React.FC<{ status: Alert["status"] }> = ({ status }) => {
  const statusConfig = {
    active: { bg: "bg-red-100", text: "text-red-700", label: "Active", icon: AlertTriangle },
    investigating: { bg: "bg-blue-100", text: "text-blue-700", label: "Investigation", icon: Eye },
    resolved: { bg: "bg-green-100", text: "text-green-700", label: "Résolue", icon: CheckCircle },
    dismissed: { bg: "bg-gray-100", text: "text-gray-700", label: "Ignorée", icon: XCircle },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </span>
  );
};

const RiskScoreBadge: React.FC<{ score: number }> = ({ score }) => {
  const getColorClass = (score: number) => {
    if (score >= 80) return "bg-red-100 text-red-700";
    if (score >= 60) return "bg-orange-100 text-orange-700";
    if (score >= 40) return "bg-yellow-100 text-yellow-700";
    return "bg-green-100 text-green-700";
  };

  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${getColorClass(score)}`}>{score}%</span>;
};

// AlertItem Component
const AlertItem: React.FC<AlertItemProps> = ({ alert, isSelected, onSelect, onAction }) => {
  const isUrgent = alert.severity === "critical" || alert.severity === "high";

  return (
    <tr className={`hover:bg-gray-50 ${isUrgent ? "bg-red-50" : ""}`}>
      <td className="px-6 py-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(alert.id, e.target.checked)}
          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
        />
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center space-x-2">
          {isUrgent && <Zap className="w-4 h-4 text-red-500" />}
          <div>
            <p className="font-medium text-gray-800">{alert.title}</p>
            <p className="text-sm text-gray-600 truncate max-w-xs">{alert.description}</p>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <TypeBadge type={alert.type} />
      </td>
      <td className="px-6 py-4">
        <SeverityBadge severity={alert.severity} />
      </td>
      <td className="px-6 py-4">
        {alert.relatedUser ? (
          <div className="flex items-center space-x-2">
            <span>{alert.relatedUser.countryFlag}</span>
            <span className="text-sm">{alert.relatedUser.name}</span>
          </div>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </td>
      <td className="px-6 py-4">
        <RiskScoreBadge score={alert.riskScore} />
      </td>
      <td className="px-6 py-4">
        <StatusBadge status={alert.status} />
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onAction("view", alert)}
            className="p-1 text-gray-400 hover:text-green-600 transition-colors"
            title="Voir détails"
          >
            <Eye className="w-4 h-4" />
          </button>
          {alert.status === "active" && (
            <>
              <button
                onClick={() => onAction("investigate", alert)}
                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                title="Investiguer"
              >
                <Search className="w-4 h-4" />
              </button>
              <button
                onClick={() => onAction("resolve", alert)}
                className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                title="Résoudre"
              >
                <CheckCircle className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

// AlertsList Component
const AlertsList: React.FC<AlertsListProps> = ({
  alerts,
  filters,
  onFiltersChange,
  selectedAlerts,
  onSelectAlert,
  onSelectAll,
  onAlertAction,
  currentPage,
  totalPages,
  onPageChange,
  startIndex,
  endIndex,
  totalAlerts,
}) => {
  // Current page alerts
  const alertsPerPage = 10;
  const startIdx = (currentPage - 1) * alertsPerPage;
  const currentAlerts = alerts.slice(startIdx, startIdx + alertsPerPage);

  return (
    <>
      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par titre, utilisateur..."
                value={filters.search}
                onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
                className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          <select
            value={filters.type}
            onChange={(e) => onFiltersChange({ ...filters, type: e.target.value })}
            className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="">Tous les types</option>
            <option value="fraud">Fraude</option>
            <option value="kyc">KYC</option>
            <option value="transaction">Transaction</option>
            <option value="system">Système</option>
            <option value="compliance">Compliance</option>
          </select>

          <select
            value={filters.severity}
            onChange={(e) => onFiltersChange({ ...filters, severity: e.target.value })}
            className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="">Toutes les sévérités</option>
            <option value="critical">Critique</option>
            <option value="high">Élevé</option>
            <option value="medium">Moyen</option>
            <option value="low">Faible</option>
          </select>

          <select
            value={filters.status}
            onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
            className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Active</option>
            <option value="investigating">Investigation</option>
            <option value="resolved">Résolue</option>
            <option value="dismissed">Ignorée</option>
          </select>
        </div>
      </div>

      {/* Alerts Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left w-12">
                  <input
                    type="checkbox"
                    checked={selectedAlerts.length === currentAlerts.length && currentAlerts.length > 0}
                    onChange={(e) => onSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Alerte</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 w-32">Type</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 w-24">Sévérité</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 w-40">Utilisateur</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 w-24">Score</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 w-32">Statut</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentAlerts.map((alert) => (
                <AlertItem
                  key={alert.id}
                  alert={alert}
                  isSelected={selectedAlerts.includes(alert.id)}
                  onSelect={onSelectAlert}
                  onAction={onAlertAction}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Affichage {startIndex + 1}-{endIndex} sur {totalAlerts} alertes
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Précédent
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} sur {totalPages}
              </span>
              <button
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Suivant
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// AlertDetails Component (Modal)
const AlertDetails: React.FC<AlertDetailsProps> = ({
  alert,
  onClose,
  onResolve,
  onDismiss,
  onInvestigate,
  onBlock,
}) => {
  if (!alert) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">Détails de l'Alerte</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Alert Header */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <TypeBadge type={alert.type} />
                <SeverityBadge severity={alert.severity} />
                <RiskScoreBadge score={alert.riskScore} />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{alert.title}</h3>
              <p className="text-gray-600">{alert.description}</p>
            </div>
            <StatusBadge status={alert.status} />
          </div>

          {/* Alert Info */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Informations Générales</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">ID Alerte:</span>
                  <span className="font-medium">{alert.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Détectée le:</span>
                  <span className="font-medium">{alert.detectedAt}</span>
                </div>
                {alert.resolvedAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Résolue le:</span>
                    <span className="font-medium">{alert.resolvedAt}</span>
                  </div>
                )}
                {alert.assignedTo && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Assignée à:</span>
                    <span className="font-medium">{alert.assignedTo}</span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Détails Techniques</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Raison:</span>
                  <span className="font-medium">{alert.metadata.reason}</span>
                </div>
                {alert.metadata.ipAddress && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">IP:</span>
                    <span className="font-medium">{alert.metadata.ipAddress}</span>
                  </div>
                )}
                {alert.metadata.location && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Localisation:</span>
                    <span className="font-medium">{alert.metadata.location}</span>
                  </div>
                )}
                {alert.metadata.deviceInfo && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Appareil:</span>
                    <span className="font-medium">{alert.metadata.deviceInfo}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Related User */}
          {alert.relatedUser && (
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Utilisateur Impliqué</h4>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{alert.relatedUser.countryFlag}</span>
                  <div>
                    <p className="font-medium">{alert.relatedUser.name}</p>
                    <p className="text-sm text-gray-600">{alert.relatedUser.email}</p>
                    <p className="text-sm text-gray-600">{alert.relatedUser.country}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Related Transaction */}
          {alert.relatedTransaction && (
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Transaction Liée</h4>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{alert.relatedTransaction.reference}</p>
                    <p className="text-sm text-gray-600">Transaction ID: {alert.relatedTransaction.id}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">
                      {alert.relatedTransaction.amount} {alert.relatedTransaction.currency}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Additional Info */}
          {alert.metadata.additionalInfo && (
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Informations Supplémentaires</h4>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-gray-700">{alert.metadata.additionalInfo}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t">
            {alert.status === "active" && (
              <>
                <button
                  onClick={onInvestigate}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Marquer en Investigation
                </button>
                <button
                  onClick={onResolve}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Résoudre
                </button>
              </>
            )}
            {alert.status === "investigating" && (
              <button
                onClick={onResolve}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
              >
                Résoudre
              </button>
            )}
            <button
              onClick={onDismiss}
              className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Ignorer
            </button>
            {alert.relatedUser && (
              <button
                onClick={onBlock}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
              >
                Bloquer Utilisateur
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main AlertsPage Component
const AlertsPage: React.FC = () => {
  // Mock data
  const allAlerts: Alert[] = [
    {
      id: "ALT001",
      type: "fraud",
      severity: "critical",
      status: "active",
      title: "Transaction suspecte détectée",
      description: "Montant anormalement élevé pour ce profil utilisateur",
      relatedUser: {
        id: "USR001",
        name: "John Smith",
        email: "john.smith@email.com",
        country: "Canada",
        countryFlag: "🇨🇦",
      },
      relatedTransaction: {
        id: "TXN003",
        reference: "REF-240320-003",
        amount: 5000,
        currency: "CAD",
      },
      detectedAt: "2024-03-20 16:30",
      riskScore: 95,
      metadata: {
        ipAddress: "192.168.1.100",
        deviceInfo: "Chrome/Mac",
        location: "Toronto, Canada",
        reason: "Montant 10x supérieur à la moyenne",
        additionalInfo: "Premier transfert vers ce pays, montant inhabituel pour le profil",
      },
    },
    {
      id: "ALT002",
      type: "kyc",
      severity: "medium",
      status: "investigating",
      title: "Document expiré détecté",
      description: "Passeport expiré soumis pour vérification KYC",
      relatedUser: {
        id: "USR002",
        name: "Ahmed Hassan",
        email: "ahmed.hassan@email.com",
        country: "Maroc",
        countryFlag: "🇲🇦",
      },
      detectedAt: "2024-03-20 14:15",
      assignedTo: "Agent KYC #1",
      riskScore: 60,
      metadata: {
        reason: "Passeport expiré depuis 3 mois",
        additionalInfo: "Utilisateur a tenté de soumettre le même document 3 fois",
      },
    },
    {
      id: "ALT003",
      type: "transaction",
      severity: "high",
      status: "active",
      title: "Tentative de transaction multiple",
      description: "Plusieurs tentatives de transaction échouées en 5 minutes",
      relatedUser: {
        id: "USR003",
        name: "Marie Dubois",
        email: "marie.dubois@email.com",
        country: "Canada",
        countryFlag: "🇨🇦",
      },
      detectedAt: "2024-03-20 18:45",
      riskScore: 85,
      metadata: {
        ipAddress: "10.0.0.50",
        deviceInfo: "Mobile/Android",
        location: "Montreal, Canada",
        reason: "8 tentatives en 5 minutes",
        additionalInfo: "Toutes les tentatives avec des montants différents",
      },
    },
    {
      id: "ALT004",
      type: "system",
      severity: "low",
      status: "resolved",
      title: "API Interac lente",
      description: "Temps de réponse de l'API supérieur à 30 secondes",
      detectedAt: "2024-03-20 12:00",
      resolvedAt: "2024-03-20 12:30",
      riskScore: 25,
      metadata: {
        reason: "Temps de réponse: 35 secondes",
        additionalInfo: "Résolu après redémarrage du service",
      },
    },
    {
      id: "ALT005",
      type: "compliance",
      severity: "high",
      status: "active",
      title: "Transaction vers pays sanctionné",
      description: "Tentative de transfert vers un pays sous sanctions",
      relatedUser: {
        id: "USR004",
        name: "David Wilson",
        email: "david.wilson@email.com",
        country: "Canada",
        countryFlag: "🇨🇦",
      },
      relatedTransaction: {
        id: "TXN010",
        reference: "REF-240320-010",
        amount: 500,
        currency: "CAD",
      },
      detectedAt: "2024-03-20 20:15",
      riskScore: 90,
      metadata: {
        ipAddress: "172.16.0.25",
        deviceInfo: "Firefox/Windows",
        location: "Vancouver, Canada",
        reason: "Pays destinataire sous sanctions",
        additionalInfo: "Transaction automatiquement bloquée",
      },
    },
    {
      id: "ALT006",
      type: "fraud",
      severity: "medium",
      status: "dismissed",
      title: "Adresse IP suspecte",
      description: "Connexion depuis une adresse IP signalée",
      relatedUser: {
        id: "USR005",
        name: "Sophie Martin",
        email: "sophie.martin@email.com",
        country: "Canada",
        countryFlag: "🇨🇦",
      },
      detectedAt: "2024-03-20 10:30",
      resolvedAt: "2024-03-20 11:00",
      riskScore: 45,
      metadata: {
        ipAddress: "203.0.113.0",
        deviceInfo: "Safari/Mac",
        location: "Toronto, Canada",
        reason: "IP dans liste de proxy connus",
        additionalInfo: "Fausse alerte - VPN d'entreprise légitime",
      },
    },
    {
      id: "ALT007",
      type: "kyc",
      severity: "high",
      status: "investigating",
      title: "Selfie non conforme détecté",
      description: "Photo selfie ne correspond pas à la pièce d'identité",
      relatedUser: {
        id: "USR006",
        name: "Ibrahim Traore",
        email: "ibrahim.traore@email.com",
        country: "Burkina Faso",
        countryFlag: "🇧🇫",
      },
      detectedAt: "2024-03-20 15:20",
      assignedTo: "Agent KYC #2",
      riskScore: 80,
      metadata: {
        reason: "Reconnaissance faciale échouée",
        additionalInfo: "Score de correspondance: 23% (seuil minimum: 85%)",
      },
    },
    {
      id: "ALT008",
      type: "transaction",
      severity: "medium",
      status: "resolved",
      title: "Transaction en dehors des heures",
      description: "Transaction importante effectuée à 3h du matin",
      relatedUser: {
        id: "USR007",
        name: "Grace Mensah",
        email: "grace.mensah@email.com",
        country: "Ghana",
        countryFlag: "🇬🇭",
      },
      relatedTransaction: {
        id: "TXN008",
        reference: "REF-240320-008",
        amount: 800,
        currency: "CAD",
      },
      detectedAt: "2024-03-20 03:15",
      resolvedAt: "2024-03-20 09:30",
      riskScore: 55,
      metadata: {
        ipAddress: "198.51.100.42",
        deviceInfo: "Chrome/Android",
        location: "Accra, Ghana",
        reason: "Transaction hors heures normales",
        additionalInfo: "Utilisateur confirmé - urgence familiale",
      },
    },
    {
      id: "ALT009",
      type: "system",
      severity: "critical",
      status: "active",
      title: "Taux d'échec élevé Mobile Money",
      description: "Plus de 50% d'échecs sur les transactions Mobile Money",
      detectedAt: "2024-03-20 19:00",
      riskScore: 90,
      metadata: {
        reason: "Taux d'échec: 65% (seuil: 10%)",
        additionalInfo: "Problème détecté avec l'API du partenaire MTN",
      },
    },
    {
      id: "ALT010",
      type: "fraud",
      severity: "low",
      status: "resolved",
      title: "Nouveau appareil détecté",
      description: "Connexion depuis un nouvel appareil non reconnu",
      relatedUser: {
        id: "USR008",
        name: "Fatou Diallo",
        email: "fatou.diallo@email.com",
        country: "Sénégal",
        countryFlag: "🇸🇳",
      },
      detectedAt: "2024-03-20 11:45",
      resolvedAt: "2024-03-20 11:50",
      riskScore: 30,
      metadata: {
        ipAddress: "192.0.2.100",
        deviceInfo: "Edge/Windows",
        location: "Dakar, Sénégal",
        reason: "Premier accès depuis ce navigateur",
        additionalInfo: "Utilisateur a confirmé via SMS",
      },
    },
  ];

  // State management
  const [filters, setFilters] = useState<FilterState>({
    search: "",
    type: "",
    severity: "",
    status: "",
    assignedTo: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAlerts, setSelectedAlerts] = useState<string[]>([]);
  const [selectedAlertForDetails, setSelectedAlertForDetails] = useState<Alert | null>(null);
  const alertsPerPage = 10;

  // Filter logic
  const filteredAlerts = allAlerts.filter((alert) => {
    return (
      (filters.search === "" ||
        alert.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        alert.description.toLowerCase().includes(filters.search.toLowerCase()) ||
        alert.relatedUser?.name.toLowerCase().includes(filters.search.toLowerCase())) &&
      (filters.type === "" || alert.type === filters.type) &&
      (filters.severity === "" || alert.severity === filters.severity) &&
      (filters.status === "" || alert.status === filters.status)
    );
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredAlerts.length / alertsPerPage);
  const startIndex = (currentPage - 1) * alertsPerPage;
  const endIndex = Math.min(startIndex + alertsPerPage, filteredAlerts.length);

  // Event handlers
  const handleSelectAll = (checked: boolean) => {
    const currentAlerts = filteredAlerts.slice(startIndex, startIndex + alertsPerPage);
    if (checked) {
      setSelectedAlerts(currentAlerts.map((alert) => alert.id));
    } else {
      setSelectedAlerts([]);
    }
  };

  const handleSelectAlert = (alertId: string, checked: boolean) => {
    if (checked) {
      setSelectedAlerts([...selectedAlerts, alertId]);
    } else {
      setSelectedAlerts(selectedAlerts.filter((id) => id !== alertId));
    }
  };

  const handleAlertAction = (action: string, alert: Alert) => {
    console.log(`Action: ${action} for alert:`, alert.id);
    if (action === "view") {
      setSelectedAlertForDetails(alert);
    }
    // Handle other actions (investigate, resolve, dismiss, block) here
  };

  const handleResolve = () => {
    console.log("Resolving alert:", selectedAlertForDetails?.id);
    setSelectedAlertForDetails(null);
  };

  const handleDismiss = () => {
    console.log("Dismissing alert:", selectedAlertForDetails?.id);
    setSelectedAlertForDetails(null);
  };

  const handleInvestigate = () => {
    console.log("Investigating alert:", selectedAlertForDetails?.id);
    setSelectedAlertForDetails(null);
  };

  const handleBlock = () => {
    console.log("Blocking user from alert:", selectedAlertForDetails?.id);
    setSelectedAlertForDetails(null);
  };

  // Calculate stats
  const activeAlerts = allAlerts.filter((a) => a.status === "active").length;
  const criticalAlerts = allAlerts.filter((a) => a.severity === "critical").length;
  const investigatingAlerts = allAlerts.filter((a) => a.status === "investigating").length;
  const avgRiskScore = Math.round(allAlerts.reduce((sum, a) => sum + a.riskScore, 0) / allAlerts.length);

  return (
      <div className="p-2 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Shield className="w-8 h-8 mt-2 text-green-600" />
              Alertes & Détection de Fraude
            </h1>
            <p className="text-gray-600 mt-1">Surveiller et gérer en temps réel les alertes de sécurité</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Alertes Actives</p>
                <p className="text-2xl font-bold text-red-600">{activeAlerts}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Critiques</p>
                <p className="text-2xl font-bold text-orange-600">{criticalAlerts}</p>
              </div>
              <Zap className="w-8 h-8 text-orange-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">En Investigation</p>
                <p className="text-2xl font-bold text-blue-600">{investigatingAlerts}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Score Risque Moyen</p>
                <p className="text-2xl font-bold text-purple-600">{avgRiskScore}%</p>
              </div>
              <Shield className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Alerts List */}
        <AlertsList
          alerts={filteredAlerts}
          filters={filters}
          onFiltersChange={setFilters}
          selectedAlerts={selectedAlerts}
          onSelectAlert={handleSelectAlert}
          onSelectAll={handleSelectAll}
          onAlertAction={handleAlertAction}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          startIndex={startIndex}
          endIndex={endIndex}
          totalAlerts={filteredAlerts.length}
        />

        {/* Alert Details Modal */}
        <AlertDetails
          alert={selectedAlertForDetails}
          onClose={() => setSelectedAlertForDetails(null)}
          onResolve={handleResolve}
          onDismiss={handleDismiss}
          onInvestigate={handleInvestigate}
          onBlock={handleBlock}
        />
      </div>
  );
};

export default AlertsPage;
